import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Receipt as ReceiptIcon,
  School as SchoolIcon,
  AttachMoney as AttachMoneyIcon
} from '@mui/icons-material';
import { esES } from '@mui/x-data-grid/locales';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { additionalPaymentService } from '../../services/additionalPaymentService';
import { exportToExcel } from '../../utils/excelExport';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
dayjs.locale('es');

const AdditionalPaymentList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState({
    total_amount: 0,
    total_count: 0,
    pending_amount: 0,
    pending_count: 0
  });
  const [concepts, setConcepts] = useState<Record<string, string>>({});
  const [loadingConcepts, setLoadingConcepts] = useState(false);
  
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });
  
  const [filters, setFilters] = useState({
    status: '',
    concept: '',
    start_date: '',
    end_date: '',
    search: ''
  });
  
  const [selectedPayment, setSelectedPayment] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  useEffect(() => {
    loadPayments();
    loadConcepts();
  }, [paginationModel, filters]);

  const loadPayments = async () => {
    try {
      setLoading(true);
      
      const params: any = {
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
        ...filters
      };
      
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await additionalPaymentService.getAll(params);
      
      setPayments(response.data.payments?.data || []);
      setTotal(response.data.payments?.total || 0);
      setTotals(response.data.totals || {
        total_amount: 0,
        total_count: 0,
        pending_amount: 0,
        pending_count: 0
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar pagos adicionales');
    } finally {
      setLoading(false);
    }
  };

  // 👈 Función para cargar conceptos desde el servidor
  const loadConcepts = async () => {
    try {
      setLoadingConcepts(true);
      const response = await additionalPaymentService.getConcepts();
      setConcepts(response.data);
    } catch (err) {
      console.error('Error loading concepts:', err);
      // Valores por defecto en caso de error
      setConcepts({
        'Matrícula': 'Pago de matrícula anual',
        'Materiales': 'Materiales educativos',
        'Excursión': 'Actividades extracurriculares',
        'Uniforme': 'Uniforme escolar',
        'Examen': 'Exámenes internacionales',
        'Biblioteca': 'Uso de biblioteca',
        'Tecnología': 'Equipos tecnológicos',
        'Deporte': 'Actividades deportivas',
        'Otro': 'Otros conceptos'
      });
    } finally {
      setLoadingConcepts(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPayment) return;
    
    try {
      await additionalPaymentService.delete(selectedPayment.id);
      loadPayments();
      setDeleteDialogOpen(false);
      setSelectedPayment(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al eliminar pago');
    }
  };

  const handleConfirm = async () => {
    if (!selectedPayment) return;
    
    try {
      await additionalPaymentService.markAsPaid(selectedPayment.id);
      loadPayments();
      setConfirmDialogOpen(false);
      setSelectedPayment(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al confirmar pago');
    }
  };

  const handleExport = () => {
    const data = payments.map(p => ({
      'ID': p.id,
      'Estudiante': p.student?.first_name + ' ' + p.student?.last_name,
      'Concepto': p.concept,
      'Descripción': p.description || '',
      'Monto': p.amount,
      'Fecha': dayjs(p.payment_date).format('DD/MM/YYYY'),
      'Método': p.payment_method === 'cash' ? 'Efectivo' :
                p.payment_method === 'bank_transfer' ? 'Transferencia' :
                p.payment_method === 'card' ? 'Tarjeta' : 'Otro',
      'Referencia': p.reference_number || '',
      'Estado': p.status === 'paid' ? 'Pagado' : p.status === 'pending' ? 'Pendiente' : 'Cancelado'
    }));
    
    exportToExcel(data, 'pagos_adicionales');
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'paid':
        return <Chip icon={<CheckCircleIcon />} label="Pagado" color="success" size="small" />;
      case 'pending':
        return <Chip icon={<ErrorIcon />} label="Pendiente" color="warning" size="small" />;
      case 'cancelled':
        return <Chip icon={<WarningIcon />} label="Cancelado" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const formatCurrency = (amount: number) => {
    return `₽ ${amount.toLocaleString()}`;
  };

  const conceptOptions = Object.keys(concepts).map(concept => ({
    value: concept,
    label: concept
  }));


  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'student',
      headerName: 'Estudiante',
      width: 200,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: 'primary.main' }}>
            <SchoolIcon fontSize="small" />
          </Avatar>
          <Typography variant="body2" fontWeight="medium">
            {params.row.student?.first_name} {params.row.student?.last_name}
          </Typography>
        </Box>
      )
    },
    {
      field: 'concept',
      headerName: 'Concepto',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          color="primary"
        />
      )
    },
    {
      field: 'description',
      headerName: 'Descripción',
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'amount',
      headerName: 'Monto',
      width: 120,
      align: 'right',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold" color="success.main">
          {formatCurrency(params.value)}
        </Typography>
      )
    },
    {
      field: 'payment_date',
      headerName: 'Fecha',
      width: 110,
      renderCell: (params) => (
        <Typography variant="body2">
          {dayjs(params.value).format('DD/MM/YYYY')}
        </Typography>
      )
    },
    {
      field: 'payment_method',
      headerName: 'Método',
      width: 130,
      renderCell: (params) => {
        const methodMap: Record<string, string> = {
          cash: 'Efectivo',
          bank_transfer: 'Transferencia',
          card: 'Tarjeta',
          other: 'Otro'
        };
        return (
          <Chip
            label={methodMap[params.value] || params.value || '-'}
            size="small"
            variant="outlined"
          />
        );
      }
    },
    {
      field: 'status',
      headerName: 'Estado',
      width: 120,
      renderCell: (params) => getStatusChip(params.value)
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 180,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver detalles">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/additional-payments/${params.row.id}`);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              onClick={() => navigate(`/additional-payments/${params.row.id}/edit`)}
              disabled={!['admin', 'manager'].includes(user?.role || '')}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.status === 'pending' && (
            <Tooltip title="Marcar como pagado">
              <IconButton
                size="small"
                color="success"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedPayment(params.row);
                  setConfirmDialogOpen(true);
                }}
              >
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPayment(params.row);
                setDeleteDialogOpen(true);
              }}
              disabled={!['admin', 'manager'].includes(user?.role || '')}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Pagos Adicionales
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{ mr: 1 }}
          >
            Exportar
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/additional-payments/new')}
            disabled={!['admin', 'manager'].includes(user?.role || '')}
          >
            Nuevo Pago
          </Button>
        </Box>
      </Box>

      {/* Tarjetas de resumen */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
            <CardContent>
              <Typography variant="body2">Total Recaudado</Typography>
              <Typography variant="h4">
                {formatCurrency(totals.total_amount)}
              </Typography>
              <Typography variant="caption">{totals.total_count} pagos</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
            <CardContent>
              <Typography variant="body2">Pendientes</Typography>
              <Typography variant="h4">
                {formatCurrency(totals.pending_amount)}
              </Typography>
              <Typography variant="caption">{totals.pending_count} pagos</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Promedio</Typography>
              <Typography variant="h4">
                {formatCurrency(totals.total_amount / (totals.total_count || 1))}
              </Typography>
              <Typography variant="caption">por pago</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar estudiante..."
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Concepto</InputLabel>
              <Select
                value={filters.concept}
                label="Concepto"
                onChange={(e) => setFilters({...filters, concept: e.target.value})}
                disabled={loadingConcepts}
              >
                <MenuItem value="">Todos</MenuItem>
                {conceptOptions.map((concept) => (
                  <MenuItem key={concept.value} value={concept.value}>
                    {concept.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              fullWidth
              type="date"
              label="Fecha Inicio"
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{
                pattern: "\\d{2}/\\d{2}/\\d{4}",
                placeholder: "DD/MM/YYYY"
              }}
              value={filters.start_date}
              onChange={(e) => setFilters({...filters, start_date: e.target.value})}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              fullWidth
              type="date"
              label="Fecha Fin"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={filters.end_date}
              onChange={(e) => setFilters({...filters, end_date: e.target.value})}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado</InputLabel>
              <Select
                value={filters.status}
                label="Estado"
                onChange={(e) => setFilters({...filters, status: e.target.value})}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="paid">Pagado</MenuItem>
                <MenuItem value="pending">Pendiente</MenuItem>
                <MenuItem value="cancelled">Cancelado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 1 }}>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                startIcon={<FilterListIcon />}
                onClick={() => loadPayments()}
                fullWidth
                size="small"
              >
                
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setFilters({ status: '', concept: '', start_date: '', end_date: '', search: '' });
                }}
                size="small"
              >
                <RefreshIcon />
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabla */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={payments}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={total}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onRowClick={(params) => navigate(`/additional-payments/${params.id}`)}
          disableRowSelectionOnClick
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        />
      </Paper>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro de eliminar este pago adicional?
          </Typography>
          {selectedPayment && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Concepto:</strong> {selectedPayment.concept}
              </Typography>
              <Typography variant="body2">
                <strong>Estudiante:</strong> {selectedPayment.student?.first_name} {selectedPayment.student?.last_name}
              </Typography>
              <Typography variant="body2">
                <strong>Monto:</strong> {formatCurrency(selectedPayment.amount)}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirmar Pago</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            ¿Confirmar este pago? Al confirmarlo, se registrará en el balance general.
          </Typography>
          {selectedPayment && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Concepto:</strong> {selectedPayment.concept}
              </Typography>
              <Typography variant="body2">
                <strong>Estudiante:</strong> {selectedPayment.student?.first_name} {selectedPayment.student?.last_name}
              </Typography>
              <Typography variant="body2">
                <strong>Monto:</strong> {formatCurrency(selectedPayment.amount)}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleConfirm} color="success" variant="contained">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdditionalPaymentList;